import React, { useState } from "react";
import PageHeader from "../Components/PageHeader";
import { CiLocationOn, CiMail, CiPhone } from "react-icons/ci";
import { MdAlternateEmail, MdSend } from "react-icons/md";
import { FaUser, FaBuilding, FaComment } from "react-icons/fa";
import { contactData } from "../Data/Data";
import useChangeTitle from "../Hooks/useChangeTitle.jsx";
import { sendContactEmail } from "../utils/emailService";

function Contact() {
  useChangeTitle("Contact");
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    service: '',
    message: ''
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);
    
    try {
      const result = await sendContactEmail(formData);
      
      if (result.success) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', company: '', service: '', message: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('Failed to send email:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
      // Clear status after 5 seconds
      setTimeout(() => setSubmitStatus(null), 5000);
    }
  };

  return (
    <>
      <PageHeader heading={contactData.heading} />
      
      {/* Hero Section */}
      <div className="relative bg-gradient-to-br from-blue-900 via-blue-800 to-blue-600 text-white overflow-hidden">
        <div className="absolute inset-0 bg-black opacity-30"></div>
        <div className="relative z-10 py-24 px-6">
          <div className="max-w-6xl mx-auto text-center">
            <h1 className="text-5xl font-bold mb-8">
              Let's Build Something 
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 to-orange-500">
                Amazing Together
              </span>
            </h1>
            <p className="text-xl mb-12 text-blue-100 max-w-3xl mx-auto leading-relaxed">
              Ready to start your next project? Get in touch with our expert team for quality 
              electromechanical services at competitive prices.
            </p>
          </div>
        </div>
        <div className="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-white to-transparent"></div>
      </div>

      {/* Contact Form and Info Section */}
      <div className="py-24 bg-gradient-to-br from-gray-50 to-blue-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-20">
            
            {/* Contact Form */}
            <div className="relative">
              <div className="absolute -inset-4 bg-gradient-to-r from-blue-400 to-blue-600 rounded-3xl blur opacity-20"></div>
              <div className="relative bg-white rounded-2xl shadow-2xl p-10 lg:p-12">
                <div className="mb-10">
                  <h2 className="text-3xl font-bold text-gray-800 mb-6">Send Us a Message</h2>
                  <p className="text-gray-600 text-lg">Fill out the form below and we'll get back to you within 24 hours.</p>
                </div>
                
                {submitStatus === 'success' && (
                  <div className="mb-6 p-4 bg-green-100 border border-green-400 text-green-700 rounded-lg flex items-center">
                    <MdSend className="mr-3 text-xl" />
                    <span>Thank you! Your message has been sent successfully.</span>
                  </div>
                )}
                
                {submitStatus === 'error' && (
                  <div className="mb-6 p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg flex items-center">
                    <MdSend className="mr-3 text-xl" />
                    <span>Sorry, there was an error sending your message. Please try again or contact us directly.</span>
                  </div>
                )}
                
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="relative">
                      <FaUser className="absolute left-4 top-4 text-gray-400" />
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        className="w-full pl-12 pr-4 py-4 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300 bg-gray-50 focus:bg-white"
                        placeholder="Your Full Name"
                      />
                    </div>
                    <div className="relative">
                      <MdAlternateEmail className="absolute left-4 top-4 text-gray-400" />
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="w-full pl-12 pr-4 py-4 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300 bg-gray-50 focus:bg-white"
                        placeholder="Your Email Address"
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="relative">
                      <FaBuilding className="absolute left-4 top-4 text-gray-400" />
                      <input
                        type="text"
                        name="company"
                        value={formData.company}
                        onChange={handleChange}
                        className="w-full pl-12 pr-4 py-4 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300 bg-gray-50 focus:bg-white"
                        placeholder="Company Name (Optional)"
                      />
                    </div>
                    <div className="relative">
                      <select
                        name="service"
                        value={formData.service}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-4 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300 bg-gray-50 focus:bg-white appearance-none"
                      >
                        <option value="">Select Service</option>
                        <option value="HVAC">HVAC Systems</option>
                        <option value="Electrical">Electrical Systems</option>
                        <option value="Plumbing">Plumbing Systems</option>
                        <option value="Light Current">Light Current Systems</option>
                        <option value="Decoration">Interior Decoration</option>
                        <option value="Maintenance">Maintenance Services</option>
                        <option value="Consultation">Project Consultation</option>
                      </select>
                    </div>
                  </div>
                  
                  <div className="relative">
                    <FaComment className="absolute left-4 top-4 text-gray-400" />
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows="6"
                      className="w-full pl-12 pr-4 py-4 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300 bg-gray-50 focus:bg-white resize-none"
                      placeholder="Tell us about your project requirements..."
                    ></textarea>
                  </div>
                  
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white font-semibold py-4 px-8 rounded-xl transform hover:scale-105 transition-all duration-300 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                  >
                    {isSubmitting ? (
                      <>
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-3"></div>
                        Sending Message...
                      </>
                    ) : (
                      <>
                        <MdSend className="mr-3 text-xl" />
                        Send Message
                      </>
                    )}
                  </button>
                </form>
              </div>
            </div>
            
            {/* Contact Information */}
            <div className="space-y-8">
              <div>
                <h2 className="text-3xl font-bold text-gray-800 mb-6">Get In Touch</h2>
                <p className="text-gray-600 text-lg leading-relaxed mb-8">
                  We're here to help you bring your projects to life. Whether you need consultation, 
                  installation, or maintenance services, our expert team is ready to assist.
                </p>
              </div>
              
              {/* Contact Cards */}
              <div className="space-y-6">
                <div className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-shadow duration-300 border-l-4 border-blue-500">
                  <div className="flex items-center mb-4">
                    <CiLocationOn className="text-3xl text-blue-500 mr-4" />
                    <div>
                      <h3 className="font-bold text-lg text-gray-800">Our Location</h3>
                      <p className="text-gray-600">{contactData.contactDetails.address}</p>
                    </div>
                  </div>
                </div>
                
                {contactData.contactDetails.emails.map((email, index) => (
                  <div key={index} className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-shadow duration-300 border-l-4 border-green-500">
                    <div className="flex items-center mb-4">
                      <MdAlternateEmail className="text-3xl text-green-500 mr-4" />
                      <div>
                        <h3 className="font-bold text-lg text-gray-800">{email.name}</h3>
                        <a
                          href={email.emailLink}
                          className="text-green-600 hover:text-green-700 transition-colors font-medium"
                        >
                          {email.emailAddress}
                        </a>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              
              {/* Quick Stats */}
              <div className="grid grid-cols-2 gap-4 mt-8">
                <div className="bg-gradient-to-br from-blue-500 to-blue-600 text-white p-6 rounded-xl text-center">
                  <h3 className="text-3xl font-bold mb-2">24h</h3>
                  <p className="text-blue-100">Response Time</p>
                </div>
                <div className="bg-gradient-to-br from-green-500 to-green-600 text-white p-6 rounded-xl text-center">
                  <h3 className="text-3xl font-bold mb-2">50+</h3>
                  <p className="text-green-100">Projects Completed</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Call to Action */}
      <div className="bg-gradient-to-r from-gray-900 via-blue-900 to-gray-900 text-white py-16">
        <div className="max-w-4xl mx-auto text-center px-4">
          <h2 className="text-3xl font-bold mb-4">Ready to Start Your Next Project?</h2>
          <p className="text-xl text-gray-300 mb-8">
            Join our satisfied clients and experience the Rakan EG difference.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="tel:+201126090982" className="bg-green-500 hover:bg-green-600 text-white px-8 py-3 rounded-full font-semibold transition-colors flex items-center justify-center">
              <CiPhone className="mr-2 text-xl" />
              Call Us Now
            </a>
            <a href="mailto:info@rakan-eg.com" className="border-2 border-white text-white hover:bg-white hover:text-gray-900 px-8 py-3 rounded-full font-semibold transition-all flex items-center justify-center">
              <CiMail className="mr-2 text-xl" />
              Email Us
            </a>
          </div>
        </div>
      </div>
    </>
  );
}

export default Contact;
